import type { AntiochWorkspace } from '../types/rome';
import { WorkspaceState } from '../types/rome';
import type { WorkspaceStreamTarget } from '../types/workspace';

// Private network patterns for local/development detection
const PRIVATE_NETWORK_PATTERNS = [
  /^10\.\d+\.\d+\.\d+$/,
  /^192\.168\.\d+\.\d+$/,
  /^100\.(6[4-9]|[7-9]\d|1[01]\d|12[0-7])\.\d+\.\d+$/, // Tailscale CGNAT
];

// Host for local development services (Isaac Sim, simulation server, Jupyter).
// Defaults to 127.0.0.1 for on-desktop use. Set VITE_STREAM_HOST to a
// Tailscale IP (e.g. 100.x.y.z) when developing over SSH so the browser's
// WebRTC client can reach Isaac Sim over the Tailscale mesh.
const LOCAL_DEV_HOST: string = import.meta.env.VITE_STREAM_HOST || '127.0.0.1';

// Fixed protocol ports for local development services
const LOCAL_SIGNALING_PORT = 49100;
const LOCAL_MEDIA_PORT = 47998;
const LOCAL_HTTP_PORT = 8081;
const LOCAL_JUPYTER_PORT = 8888;
const LOCAL_RERUN_PORT = 9876;

// Local workspace identity
export const LOCAL_WORKSPACE_ID = 'ws-local-dev';
const LOCAL_WORKSPACE_NAME = 'Local Development';

/**
 * Extract hostname from a workspace URL.
 * Handles wss://, https://, or bare hostname formats.
 */
export function extractHostname(url: string): string {
  let hostname = url;
  hostname = hostname.replace(/^wss?:\/\//, '').replace(/^https?:\/\//, '');
  hostname = hostname.replace(/:\d+.*$/, '').replace(/\/.*$/, '');
  return hostname;
}

/**
 * Check if a hostname is local or on a private network (no auth required).
 *
 * ``local.console.antioch.com`` is an /etc/hosts alias for 127.0.0.1 used
 * for HTTPS local dev; it's same-site with the deployed console domains so
 * Auth0 persists consent grants there (see ``scripts/ensure-https-certs.js``).
 */
export function isLocalHost(hostname: string): boolean {
  if (hostname === '127.0.0.1' || hostname === 'localhost' || hostname === 'local.console.antioch.com') {
    return true;
  }
  return PRIVATE_NETWORK_PATTERNS.some((pattern) => pattern.test(hostname));
}

/**
 * Check if a stream target is for local/private development (no auth required).
 */
export function isLocalTarget(target: WorkspaceStreamTarget): boolean {
  return isLocalHost(target.signaling.host);
}

/**
 * Build a WorkspaceStreamTarget from Rome workspace data.
 *
 * @param workspaceUrl - The Cloudflare-proxied hostname
 * @param staticIp - The workspace's external IP. Carries TCP (HTTP/SSH)
 *     and UDP (WebRTC media) on the same address since the chart uses a
 *     single mixed-protocol LoadBalancer Service. Required for cloud
 *     workspaces.
 */
export function buildTargetFromWorkspace(workspaceUrl: string, staticIp: string): WorkspaceStreamTarget {
  const hostname = extractHostname(workspaceUrl);

  return {
    signaling: {
      host: hostname,
      port: 443,
      path: '/streaming/',
    },
    media: {
      host: staticIp,
      port: 47998,
    },
    httpBaseUrl: `https://${hostname}`,
    sshHost: staticIp,
  };
}

/**
 * Build the JupyterLab URL with JWT token for authentication.
 *
 * For local workspaces, Jupyter runs directly on port 8888 with the standard path.
 * For cloud workspaces, Jupyter is proxied through the workspace hostname at /jupyter/.
 */
export function buildJupyterUrl(workspaceUrl: string, workspaceToken?: string): string {
  const hostname = extractHostname(workspaceUrl);
  const isLocal = isLocalHost(hostname);

  // Local: direct Jupyter server on port 8888
  // Cloud: proxied through workspace hostname at /jupyter/
  const baseUrl = isLocal ? `http://${hostname}:8888/lab` : `https://${hostname}/jupyter/`;

  if (workspaceToken) {
    return `${baseUrl}?token=${encodeURIComponent(workspaceToken)}`;
  }

  return baseUrl;
}

/**
 * Build the Rerun gRPC connection URL for the embedded web viewer.
 *
 * Returns a gRPC URL that the @rerun-io/web-viewer component connects
 * to for live telemetry streaming. This URL uses the rerun+http(s)
 * scheme and is NOT a valid HTTP URL.
 *
 * Rerun's WASM URL parser (re_uri) only accepts `/proxy`,
 * `/catalog`, `/entry/<id>`, or `/dataset/<id>` as paths -- any prefix
 * like `/rerun-grpc/proxy` is rejected. Authentication is handled via
 * the antioch_auth cookie (seeded by the probe, sent via credential
 * patching in RerunViewer).
 */
export function buildRerunUrl(workspaceUrl: string): string {
  const hostname = extractHostname(workspaceUrl);
  if (isLocalHost(hostname)) {
    return `rerun+http://${LOCAL_DEV_HOST}:${LOCAL_RERUN_PORT}/proxy`;
  }
  return `rerun+https://${hostname}/proxy`;
}

/**
 * Build the HTTP(S) probe URL for checking Rerun gRPC server availability.
 *
 * For cloud workspaces the workspace token is appended as a query
 * parameter so Envoy's JWT filter can authenticate the probe request
 * and the Lua filter can set the antioch_auth cookie for subsequent
 * WASM gRPC-web requests.
 */
export function buildRerunProbeUrl(workspaceUrl: string, workspaceToken?: string): string {
  const hostname = extractHostname(workspaceUrl);
  if (isLocalHost(hostname)) {
    return `http://${LOCAL_DEV_HOST}:${LOCAL_RERUN_PORT}/proxy`;
  }
  const base = `https://${hostname}/rerun-grpc/proxy`;
  if (workspaceToken) {
    return `${base}?token=${encodeURIComponent(workspaceToken)}`;
  }
  return base;
}

/**
 * Build the WebRTC streaming websocket URL.
 */
export function buildStreamingUrl(workspaceUrl: string, streamingToken?: string): string {
  const hostname = extractHostname(workspaceUrl);
  const baseUrl = `wss://${hostname}/streaming/`;

  if (streamingToken) {
    return `${baseUrl}?token=${encodeURIComponent(streamingToken)}`;
  }

  return baseUrl;
}

/**
 * Build the stream target for the local development workspace.
 *
 * All ports are fixed protocol constants. The host is resolved from
 * VITE_STREAM_HOST (for Tailscale SSH) or defaults to 127.0.0.1.
 */
export function getLocalStreamTarget(): WorkspaceStreamTarget {
  return {
    signaling: {
      host: LOCAL_DEV_HOST,
      port: LOCAL_SIGNALING_PORT,
      path: '',
    },
    media: {
      host: LOCAL_DEV_HOST,
      port: LOCAL_MEDIA_PORT,
    },
    httpBaseUrl: `http://${LOCAL_DEV_HOST}:${LOCAL_HTTP_PORT}`,
    sshHost: LOCAL_DEV_HOST,
  };
}

/**
 * Build the AntiochWorkspace entry for the local development workspace.
 *
 * Displayed in the workspace table when running in local development mode.
 * The host is resolved from VITE_STREAM_HOST or defaults to 127.0.0.1.
 */
export function getLocalWorkspace(): AntiochWorkspace {
  return {
    id: LOCAL_WORKSPACE_ID,
    name: LOCAL_WORKSPACE_NAME,
    hostname: LOCAL_DEV_HOST,
    ipAddress: LOCAL_DEV_HOST,
    createdAt: Math.floor(Date.now() / 1000),
    state: WorkspaceState.RUNNING,
  };
}

/**
 * Get the Jupyter URL for the local development workspace.
 */
export function getLocalJupyterUrl(): string {
  return `http://${LOCAL_DEV_HOST}:${LOCAL_JUPYTER_PORT}`;
}

/**
 * Check if running in local/private-network development mode.
 *
 * Returns true for localhost, private RFC-1918 ranges, and Tailscale CGNAT
 * IPs so the local workspace config is available when accessing the webapp
 * over SSH port-forwarding or directly via a Tailscale address.
 */
export function isLocalDevelopment(): boolean {
  return typeof window !== 'undefined' && isLocalHost(window.location.hostname);
}
